import React, { useState } from 'react';

import { countries } from '../../constants';
import { useAppState } from '../../state';
import { apiSendMail, apiSendSms } from '../../utils/api';
import { makeStyles } from '@mui/styles';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  Grid2,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Theme,
} from '@mui/material';
import { RoomPrivacyType } from '../../types';

const useStyles = makeStyles((theme: Theme) => ({
  inputContainer: {
    marginTop: '28px',
  },
  textField: {
    width: '350px',
    display: 'block',
  },
  input: {
    letterSpacing: '.5px',
    // padding: '12px'
  },
  errMsg: {
    fontSize: '14px',
    color: 'red',
  },
}));

interface ManualInviteProps {
  propClasses: any;
  deviceSelectionScreen: boolean | undefined;
  handleClose: (isInviteSent: boolean) => void;
  connect: (args: {
    privacy: RoomPrivacyType;
    roomName: string;
    shouldRecord: boolean;
    username: string;
    skipRoomCheck?: boolean;
  }) => Promise<void>;
  shouldRecord?: boolean;
}

const ManualInvite = ({
  propClasses,
  connect,
  deviceSelectionScreen,
  handleClose,
  shouldRecord,
}: ManualInviteProps) => {
  const classes = useStyles();
  const { username, domain, roomName, ownRoomDid } = useAppState();
  const [mode, setMode] = useState<string>('email');
  const [inputVal, setInputVal] = useState<string>('');
  const [inputName, setInputName] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [countryCode, setCountryCode] = useState<string>(countries[0].cc);

  // fires when value of name changes
  const handleChange = (e: SelectChangeEvent<string>) => {
    setMode(e.target.value as string);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    setInputVal(e.target.value as string);
  };

  const handleSubmit = () => {
    // input isn't good, then end
    if (!validateInput()) return;
    // input is good, start submission
    setIsSubmitting(true);
    const fromupn = `${username}@${domain}.local`;
    const promise =
      // button set to email
      mode === 'email'
        ? // set action to apiSendMail
          apiSendMail([inputVal], fromupn, ownRoomDid, [inputName.replace(/ /, '')])
        : // otherwise set action to send message
          apiSendSms(['+' + countryCode + inputVal], fromupn, [inputName.replace(/ /, '')], ownRoomDid);
    promise
      .then(async () => {
        // software is running
        if (deviceSelectionScreen) {
          await connect({
            username,
            roomName,
            privacy: 'public',
            shouldRecord: !!shouldRecord,
            skipRoomCheck: true,
          });
          return;
        }
        setIsSubmitting(false);
        handleClose(true);
      })
      .catch(e => {
        setIsSubmitting(false);
        handleClose(false);
        console.log(e);
      });
  };

  const validateInput = () => {
    const emailRegex = /^\S*@\S{1,}\.[a-z]{2,}$/;
    const phoneRegex = /[1-9]{1}[0-9]{9}/;
    // if input isn't valid regex, then throw error
    if ((mode === 'email' && !emailRegex.test(inputVal)) || (mode === 'sms' && !phoneRegex.test(inputVal))) {
      setError(`Please enter a valid ${mode === 'email' ? 'email' : 'number'}`);
      return false;
    }
    return true;
  };

  const onCloseClick = () => {
    handleClose(false);
  };

  // fires whenever value of cc is changed
  const handleCountryCodeChange = (e: SelectChangeEvent<string>) => {
    setCountryCode(e.target.value as string);
  };

  // allows shift, caps lock, and space while typing input
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const keys = [16, 20, 32];
    if ((e.which >= 65 && e.which <= 90) || (e.which >= 97 && e.which <= 122) || keys.includes(e.which)) {
      console.log(e.which);
    } else {
      e.preventDefault();
    }
  };

  return (
    <>
      <DialogContent className={propClasses.contentContainer}>
        <FormControl variant="outlined">
          <Select labelId="invite-type-label" id="invite-type-select" value={mode} onChange={handleChange}>
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="sms">SMS</MenuItem>
          </Select>
        </FormControl>
        <div className={classes.inputContainer}>
          <Grid2 container gap={1}>
            {mode === 'sms' && (
              <Select
                labelId="sms-type-label"
                id="sms-type-invite"
                value={countryCode}
                onChange={handleCountryCodeChange}
              >
                {countries.map(country => (
                  <MenuItem value={country.cc} key={country.cc}>
                    +{country.cc}
                  </MenuItem>
                ))}
              </Select>
            )}
            <TextField
              id="email-sms-input-label"
              label={mode === 'email' ? 'Enter email' : 'Enter number'}
              inputProps={{ 'aria-label': 'number' }}
              value={inputVal}
              onChange={handleInputChange}
              InputProps={{ classes: { input: classes.input } }}
              // slotProps={{ input: { classes: { input: classes.input } } }}
              classes={{ root: classes.textField }}
              fullWidth
            />
          </Grid2>
          {error && (
            <pre>
              <code className={classes.errMsg}>{error}</code>
            </pre>
          )}
          <TextField
            fullWidth
            id="standard-basic"
            label="Enter Invitee Name"
            onChange={e => setInputName(e.target.value)}
            value={inputName}
            style={{ marginTop: '32px' }}
            InputProps={{ classes: { input: classes.input } }}
            classes={{ root: classes.textField }}
            onKeyPress={handleKeyDown}
          />
        </div>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !inputName || !inputVal}
        >
          {isSubmitting ? 'Sending...' : 'Send'}
        </Button>
        <Button color="secondary" variant="contained" onClick={onCloseClick}>
          Close
        </Button>
      </DialogActions>
    </>
  );
};

export default ManualInvite;
